import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { CalendarCheckIcon, LightningIcon, TimerIcon, UsersThreeIcon, XIcon } from '@phosphor-icons/react'
import moment from 'moment'
import { Button } from 'app/components/ui/button'
import { REENROLLMENT_CONSEQUENCE } from 'app/lib/workflows/actionCatalogs'
import {
  capsSummary,
  consentSummary,
  enrollmentSentence,
  sendWindowSummary,
  stepSummary,
} from 'app/lib/workflows/activationSummary'
import { availableFieldsForTrigger } from 'app/lib/workflows/dataContext'
import type { GlobalWorkflowSettings } from 'app/lib/workflows/globalSettings'
import { Alert } from './inspectors/Alert'
import { useConditionValueOptions } from './inspectors/panelShared'
import type { TriggerNodeData, WorkflowNode, WorkflowSettings } from 'app/types/workflows'
import type { TriggerLookback } from './useTriggerLookback'

/** Who turning the workflow on enrolls straight away. */
export type GoLiveEnrollment =
  /** Manual enrollment: nobody enrolls on their own. */
  | { kind: `manual` }
  /** An event trigger with backfill off: it starts with what happens next. */
  | { kind: `fresh` }
  /** What happened, or for a date trigger came due, since the backfill day `since`. */
  | ({ kind: `preview`; since: string; dateTrigger: boolean; onView: () => void } & Pick<
      TriggerLookback,
      `status` | `total` | `loadedAt`
    >)
  /** No preview available; the card says nothing it cannot back. */
  | { kind: `unknown` }

/** A ceiling: the preview skips the engine's live checks, so some may still be skipped. */
function GoLiveLine({ goLive }: { goLive: GoLiveEnrollment }) {
  if (goLive.kind === `unknown`) return null
  const text =
    goLive.kind === `manual`
      ? `Nobody enrolls on their own. You add people.`
      : goLive.kind === `fresh`
        ? `Nobody from before now enrolls. It starts with what happens next.`
        : goLive.status === `error`
          ? `Couldn't count who enrolls right away.`
          : !goLive.loadedAt
            ? `Counting who enrolls right away…`
            : goLive.total === 0
              ? `Nobody enrolls right away.`
              : null
  if (text)
    return (
      <div className="flex items-start gap-2 text-xs leading-relaxed text-default">
        <UsersThreeIcon size={14} weight="bold" className="mt-0.5 shrink-0 text-minimal" />
        <span>{text}</span>
      </div>
    )
  if (goLive.kind !== `preview`) return null
  return (
    <Alert tone="warn" lead="Enrolls right away">
      Up to {goLive.total.toLocaleString()} {goLive.total === 1 ? `person` : `people`}
      {goLive.dateTrigger ? ` whose date came due` : ` from events`} since{` `}
      {moment(goLive.since).format(`MMM D, YYYY`)}.{` `}
      Your checks can still skip some.{` `}
      <Button variant="link" size="inline" onClick={goLive.onView} className="inline">
        See who
      </Button>
    </Alert>
  )
}

type ActivationPreflightProps = {
  name: string
  nodes: WorkflowNode[]
  settings: WorkflowSettings
  /** Workspace-wide caps (Automation settings). Loaded by the page; absent fields mean no cap. */
  caps: GlobalWorkflowSettings
  /** Only an admin's save can apply a transactional category, so the consent line depends on it. */
  canSetCategory?: boolean
  /** The category the engine holds today; absent means marketing (its own default). */
  serverCategory?: string | null
  /** Open the Automation settings modal (stacked above); the page re-reads caps after it saves. */
  onOpenCaps: () => void
  /** Who enrolls the moment it goes live. */
  goLive: GoLiveEnrollment
  /** Activate AND save in one motion — a confirmed preflight must never leave a stale draft live. */
  onConfirm: () => void
  onClose: () => void
}

/** The go-live review (UX audit B2). Turning a workflow on used to be a silent toggle plus Save;
 *  this card states, in the builder's own sentence grammar, exactly what is about to start
 *  happening — and is loud about the states that used to be silent: no send window, no caps. */
export const ActivationPreflight = observer(function ActivationPreflight({
  name,
  nodes,
  settings,
  caps,
  canSetCategory,
  serverCategory,
  onOpenCaps,
  goLive,
  onConfirm,
  onClose,
}: ActivationPreflightProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === `Escape`) onClose()
    }
    window.addEventListener(`keydown`, onKey)
    return () => window.removeEventListener(`keydown`, onKey)
  }, [onClose])

  const trigger = nodes.find((node) => node.type === `trigger`)
  const triggerData = trigger?.data as TriggerNodeData | undefined
  const valueOptions = useConditionValueOptions(triggerData ? availableFieldsForTrigger(triggerData) : [])
  const steps = stepSummary(nodes)
  const window_ = sendWindowSummary(settings.sendTimes)
  const capsLine = capsSummary(caps)
  const consent = consentSummary(settings, { canSetCategory, serverCategory })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-surface-raised shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-default px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-strong">Set “{name || `this workflow`}” live?</h2>
            <p className="text-xs text-subtle">From the moment you confirm, this runs on its own.</p>
          </div>
          <Button variant="subtle" size="icon-sm" onClick={onClose} aria-label="Close">
            <XIcon size={16} weight="bold" />
          </Button>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <div className="space-y-2.5">
            <div className="flex items-start gap-2 text-xs leading-relaxed text-default">
              <LightningIcon size={14} weight="bold" className="mt-0.5 shrink-0 text-minimal" />
              <span>{triggerData ? enrollmentSentence(triggerData, valueOptions) : `No trigger configured.`}</span>
            </div>
            {goLive.kind !== `preview` && <GoLiveLine goLive={goLive} />}
            <div className="flex items-start gap-2 text-xs leading-relaxed text-default">
              <CalendarCheckIcon size={14} weight="bold" className="mt-0.5 shrink-0 text-minimal" />
              <span>{steps.text}</span>
            </div>
            {/* Send-window and caps only matter when something actually leaves the building. */}
            {steps.hasOutbound && (
              <div className="flex items-start gap-2 text-xs leading-relaxed text-default">
                <TimerIcon size={14} weight="bold" className="mt-0.5 shrink-0 text-minimal" />
                <span>{window_.text}</span>
              </div>
            )}
          </div>

          {goLive.kind === `preview` && <GoLiveLine goLive={goLive} />}

          {steps.hasOutbound &&
            (consent.warn ? (
              <Alert tone="warn" lead="Marketing consent">
                {consent.text}
              </Alert>
            ) : (
              <Alert tone="info">{consent.text}</Alert>
            ))}

          {steps.hasOutbound && window_.warn && (
            <Alert tone="warn" lead="No send window">
              Late texts are skipped, not held. Turn the window on under Texting policy (Edit on any text step).
            </Alert>
          )}

          {steps.hasOutbound &&
            (capsLine.warn ? (
              <Alert tone="warn" lead="No volume cap">
                {capsLine.text} A cap pauses sending at a budget you choose; runs hold, not drop.{` `}
                <Button variant="link" size="inline" onClick={onOpenCaps} className="inline">
                  Set caps
                </Button>
              </Alert>
            ) : (
              <Alert tone="info">
                {capsLine.text}
                {` `}
                <Button variant="link" size="inline" onClick={onOpenCaps} className="inline">
                  Automation settings
                </Button>
              </Alert>
            ))}

          <p className="px-1 text-[11px] leading-relaxed text-subtle">
            {REENROLLMENT_CONSEQUENCE[(triggerData?.reenrollment ?? `ignore`) as string]}
          </p>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-default px-5 py-3">
          <Button variant="white" size="sm" onClick={onClose}>
            Keep as draft
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm}>
            Set live
          </Button>
        </footer>
      </div>
    </div>
  )
})
